/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef, ReactElement } from "react";
import axios from "axios";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { Select, Button } from "@chakra-ui/react";
import { FaPlus, FaAngleLeft } from "react-icons/fa";
import SweetAlert2 from "react-sweetalert2";
import { useNavigate } from "react-router-dom";
import AutoComplete from "react-select";

const CreateReceiver = () => {
  const initialData: {
    first_name: string;
    last_name: string;
    email: string;
    middle_name: string;
    dob: string;
    dialcode: string;
    phone: string;
    address: string;
    address2: string;
    gender: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
    reference: string;
  } = {
    first_name: "",
    last_name: "",
    email: "",
    middle_name: "",
    dob: "",
    dialcode: "",
    phone: "",
    address: "",
    address2: "",
    gender: "",
    city: "",
    state: "",
    postcode: "",
    country: "",
    reference: "",
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [countries, setCountries] = useState<any>(null);
  const [dialcodeList, setDialcodeList] = useState<any>(null);
  const [dialcodeAutoCompleteOptions, setDialcodeAutoCompleteOptions] =
    useState<any>(null);
  const [countryAutoCompleteOptions, setCountryAutoCompleteOptions] =
    useState<any>(null);
  const [newReceiver, setNewReceiver] = useState<any>(initialData);
  const [swalProps, setSwalProps] = useState<any>({});
  const [isDateOfBirthError, setIsDateOfBirthError] = useState<boolean>(false);
  
  const dialcodeAutoCompleteRef = useRef(null);
  const countryAutoCompleteRef = useRef(null);
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  useEffect(() => {
    const fetchData = async () => {
      await fetchCountries();
      setIsLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (dialcodeList !== null) {
      let rebuildListArray: any = [];
      
      Object.entries(dialcodeList).forEach(([key, value]: [any, any]) => {
        let newObj = {
          value: key,
          label: value,
        };
        rebuildListArray.push(newObj);
      });

      setDialcodeAutoCompleteOptions(rebuildListArray);
    }
  }, [dialcodeList]);

  useEffect(() => {
    if (countries !== null) {
      let rebuildListArray: any = [];
      
      countries.forEach((item: any) => {
        let newObj = {
          value: item.country_code_3,
          label: item.country_name,
        };
        rebuildListArray.push(newObj);
      });

      setCountryAutoCompleteOptions(rebuildListArray);
    }
  }, [countries]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/datasource/countries`
      );

      let countries = response?.data;
      let dialcodeList: any = [];
      if (countries.length > 0) {
        countries.forEach((c: any) => {
          if (c.dialcode !== null) {
            if (c.dialcode in dialcodeList) {
              dialcodeList[c.dialcode] =
                dialcodeList[c.dialcode] + ", " + c.country_name;
            } else {
              dialcodeList[c.dialcode] =
                "(+" + c.dialcode + ") " + c.country_name;
            }
          }
        });
      }

      const sortedDialcodeList: any = [];
      Object.keys(dialcodeList)
        .sort()
        .forEach((key: any) => {
          sortedDialcodeList[key] = dialcodeList[key];
        });

      setCountries(countries);
      setDialcodeList(sortedDialcodeList);
    } catch (error) {
      console.error("Error fetching countries", error);
    }
  };

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    setNewReceiver({ ...newReceiver, [id]: value });

    if (id === "dob") {
      const selectedDate = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - selectedDate.getFullYear();
      const monthDifference = today.getMonth() - selectedDate.getMonth();
      const dayDifference = today.getDate() - selectedDate.getDate();
      const isAtLeast18 = age > 18 || (age === 18 && (monthDifference > 0 || (monthDifference === 0 && dayDifference >= 0)));

      if (!isAtLeast18) {
        setIsDateOfBirthError(true);
      } else {
        setIsDateOfBirthError(false);
      }
    }
  };

  const handleDialcodeAutoCompleteChange = (item: any) => {
    if (item !== null) {
      setNewReceiver({ ...newReceiver, dialcode: item.value });
    } else {
      setNewReceiver({ ...newReceiver, dialcode: "" });
    }
  };

  const handleCountryAutoCompleteChange = (item: any) => {
    if (item !== null) {
      setNewReceiver({ ...newReceiver, country: item.value });
    } else {
      setNewReceiver({ ...newReceiver, country: "" });
    }
  };

  const noDialcodeOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return <div className="text-sm">Dialcode not found for "{inputValue}"</div>;
  };

  const noCountryOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return <div className="text-sm">Country not found for "{inputValue}"</div>;
  };

  const isFormValid =
    newReceiver !== null
      ? newReceiver.first_name &&
      newReceiver.last_name &&
      newReceiver.email &&
      newReceiver.dob &&
      newReceiver.dialcode &&
      newReceiver.phone &&
      newReceiver.address &&
      newReceiver.city &&
      newReceiver.postcode &&
      newReceiver.country &&
      !isDateOfBirthError
      : false;

  const handleSubmit = async () => {
    if (isFormValid) {
      newReceiver.subaccount_id = localStorage.getItem("subaccountId");
      newReceiver.username = localStorage.getItem("username");
      newReceiver.userRole = userRole;

      if (newReceiver.state === "") {
        delete newReceiver.state;
      }

      try {
        setIsCreating(true);
        await axios
          .post(
            process.env.REACT_APP_API_URL + `/api/${userRole}/receiver/action/create-receiver`,
            newReceiver
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: "Create receiver successfully",
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsCreating(false);
                  if (response.data?.result?.payload?.receiver_id) {
                    navigate(`/${userRole}/receiver/${response.data?.result?.payload?.receiver_id}`);
                  } else {
                    navigate(`/${userRole}/receiver`);
                  }
                },
              });
            } else {
              setIsCreating(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsCreating(false);
                },
              });
            }
          })
          .catch(function (error) {
            setIsCreating(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsCreating(false);
              },
            });
          });
      } catch (error: any) {
        setIsCreating(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsCreating(false);
          },
        });
      }
    }
  };

  return (
    <>
      <form>
        <div className="inter mx-auto max-w-5xl space-y-6 rounded-xl bg-white p-12 px-20 shadow-lg ring-1 ring-gray-100">
          <h2 className="text-left text-3xl font-bold text-navy-700">
            Create Receiver
          </h2>
          <div className="space-y-4 border-b border-gray-200 pb-10">
            <p className="text-md font-medium tracking-normal text-gray-700">
              Provide the receiver's detail
            </p>
            <div className="grid grid-cols-1 gap-8 gap-y-4 md:grid-cols-2">
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="shadow-sm"
                  label="First Name"
                  required={true}
                  placeholder="Enter first name"
                  id="first_name"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.first_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Middle Name"
                  placeholder="Enter middle name"
                  id="middle_name"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.middle_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Last Name"
                  required={true}
                  placeholder="Enter last name"
                  id="last_name"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.last_name}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800">
                  Gender
                </label>
                <Select
                  disabled={isCreating}
                  id="gender"
                  value={newReceiver.gender}
                  onChange={handleInputChange}
                  className="custom-select w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm tracking-tight shadow-sm placeholder:font-medium"
                >
                  <option value="">-- Please Select --</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </Select>
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Email"
                  required={true}
                  placeholder="Enter email address"
                  id="email"
                  type="email"
                  autoComplete="off"
                  value={newReceiver.email}
                  onChange={handleInputChange}
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Date of Birth"
                  placeholder="Select date of birth"
                  id="dob"
                  type="date"
                  autoComplete="off"
                  value={newReceiver.dob}
                  onChange={handleInputChange}
                  error={
                    isDateOfBirthError
                      ? "You must be at least 18 years old"
                      : ""
                  }
                />
              </div>
              <div>
                <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white">
                  Dialcode <span className="text-red-500">*</span>
                </label>
                <AutoComplete
                  ref={dialcodeAutoCompleteRef}
                  isDisabled={
                    isLoading ||
                    isCreating ||
                    dialcodeAutoCompleteOptions === null ||
                    dialcodeAutoCompleteOptions.length === 0
                  }
                  placeholder={
                    dialcodeAutoCompleteOptions === null
                      ? "Retrieving..."
                      : "Please Select"
                  }
                  options={dialcodeAutoCompleteOptions}
                  classNamePrefix="custom-autocomplete"
                  className="autocomplete-override"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  onChange={(item: any) => handleDialcodeAutoCompleteChange(item)}
                  noOptionsMessage={noDialcodeOptionsMessageRenderer}
                  isClearable
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Phone"
                  required={true}
                  placeholder="Enter phone number"
                  id="phone"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.phone}
                  onChange={handleInputChange}
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Address"                  
                  required={true}
                  placeholder="Enter address"
                  id="address"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.address}
                  onChange={handleInputChange}
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Address 2"
                  placeholder="Enter address line 2 (optional)"
                  id="address2"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.address2}
                  onChange={handleInputChange}
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="City"
                  required={true}
                  placeholder="Enter city"
                  id="city"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.city}
                  onChange={handleInputChange}
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="State"
                  placeholder="Enter state"
                  id="state"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.state}
                  onChange={handleInputChange}
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Postcode *"
                  placeholder="Enter postcode"
                  id="postcode"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.postcode}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white">
                  Country <span className="text-red-500">*</span>
                </label>
                <AutoComplete
                  ref={countryAutoCompleteRef}
                  isDisabled={
                    isLoading ||
                    isCreating ||
                    countryAutoCompleteOptions === null ||
                    countryAutoCompleteOptions.length === 0
                  }
                  placeholder={
                    countryAutoCompleteOptions === null
                      ? "Retrieving..."
                      : "Please Select"
                  }
                  options={countryAutoCompleteOptions}
                  classNamePrefix="custom-autocomplete"
                  className="autocomplete-override"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  onChange={(item: any) => handleCountryAutoCompleteChange(item)}
                  noOptionsMessage={noCountryOptionsMessageRenderer}
                  isClearable
                />
              </div>
              <div className="customForm">
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="mb-3 shadow-sm"
                  label="Reference"
                  placeholder="Enter reference (optional)"
                  id="reference"
                  type="text"
                  autoComplete="off"
                  value={newReceiver.reference}
                  onChange={handleInputChange}
                />
              </div>
            </div>

          </div>
          <div className="text-right">
            <Button
              onClick={() => navigate(`/${userRole}/receiver`)}
              isDisabled={isCreating}
              className="mr-4 rounded-md border border-gray-300 bg-white px-8 py-2 text-sm font-semibold tracking-tight text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 md:px-10"
            >
              Back
            </Button>
            <Button
              onClick={() => handleSubmit()}
              isDisabled={!isFormValid}
              isLoading={isCreating}
              loadingText="Creating..."
              className="btn-primary relative overflow-hidden rounded-md px-10 py-2.5 font-semibold text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="relative z-10 tracking-tight">
                Create Receiver
              </span>
              <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
            </Button>
          </div>
        </div>
      </form>
      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default CreateReceiver;